import React from 'react'
import { connect } from 'react-redux'
import { CircularProgress } from '@material-ui/core'
import axios from 'axios'
import { toast } from 'react-toastify'

const fmt$ = (v) => v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''
const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : ''

const CATEGORIES = ['All', 'Irrigation', 'Plant', 'Aggregate', 'Lighting', 'Hardscape', 'General', 'Herbicide', 'Fertilizer']
const SOURCES = [
  { value: '', label: 'All sources' },
  { value: 'manual', label: 'Manual' },
  { value: 'aspire_sync', label: 'Aspire promote' },
  { value: 'legacy_promote', label: 'Legacy promote' },
  { value: 'vendor_import', label: 'Vendor import' },
  { value: 'aggregate_import', label: 'Aggregate import' }
]

function Stat ({ label, value, caption, tone }) {
  return (
    <div className="mat-stat">
      <span className="eyebrow">{label}</span>
      <span className={`num${tone ? ' tone-' + tone : ''}`}>{value}</span>
      <span className="sub">{caption}</span>
    </div>
  )
}

function Section ({ tone, icon, title, count, note, defaultOpen = true, actions, children }) {
  const [open, setOpen] = React.useState(defaultOpen)
  if (count === 0 && !children) return null
  return (
    <section className="mat-section">
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <button className="mat-sec-head" style={{ flex: 1 }} onClick={() => setOpen(!open)}>
          <span className={`mat-sec-icon mat-sec-icon--${tone}`}>{icon}</span>
          <span className="mat-sec-title">{title}</span>
          {count != null && <span className="mat-sec-count">{count}</span>}
          <span className="mat-sec-note">{note}</span>
          <span className="mat-sec-chev">{open ? '▾' : '▸'}</span>
        </button>
        {actions && open && <div style={{ display: 'flex', gap: 6, padding: '0 12px', flexShrink: 0 }}>{actions}</div>}
      </div>
      {open && <div className="mat-sec-body">{children}</div>}
    </section>
  )
}

function CatalogItemRow ({ item, onEdit }) {
  const hasAspire = item.aspireItemIds?.length > 0
  const hasVendor = item.vendorCatalogProductIds?.length > 0
  const xrefs = []
  if (hasAspire) xrefs.push(`${item.aspireItemIds.length} Aspire`)
  if (hasVendor) xrefs.push(`${item.vendorCatalogProductIds.length} Vendor`)
  if (item.legacyTakeoffId) xrefs.push('Legacy')
  if (item.aggregateMaterialProfileId) xrefs.push('Aggregate')
  const name = item.canonicalName || item.description || item.key || '(unnamed)'
  const cov = ['covered', 'partial', 'missing'].includes(item.coverageStatus) ? item.coverageStatus : 'missing'

  return (
    <div className="mat-row" onClick={() => onEdit && onEdit(item)} style={{ cursor: onEdit ? 'pointer' : 'default' }}>
      <div className="mat-row-main">
        <div className="mat-row-title">
          <span className="mat-row-name">{name}</span>
          <span className="mat-row-meta">{cap(item.category)} · {item.unit}</span>
        </div>
        <div className="mat-row-sub">
          <SourceBadge source={item.source} />
          {xrefs.length > 0 && <><span className="dot-sep">·</span><span>{xrefs.join(', ')}</span></>}
          {item.aliases?.length > 0 && <><span className="dot-sep">·</span><span style={{ fontStyle: 'italic' }}>aka {item.aliases.slice(0, 3).join(', ')}</span></>}
        </div>
      </div>
      <div className="mat-row-right">
        {item.bestPrice > 0 && <span className="mat-row-price">{fmt$(item.bestPrice)}</span>}
        {item.bestPrice > 0 && <span className="mat-row-uom">/ {item.unit}</span>}
        <span className={`mat-pill mat-pill--${cov === 'covered' ? 'ok' : cov === 'partial' ? 'warn' : 'risk'}`}>
          <span className="mat-pill-dot" />{cap(cov)}
        </span>
      </div>
    </div>
  )
}

function SourceBadge ({ source }) {
  const labels = {
    manual: 'Manual',
    aspire_sync: 'Aspire',
    legacy_promote: 'Legacy',
    vendor_import: 'Vendor',
    aggregate_import: 'Aggregate'
  }
  const colors = {
    manual: 'var(--ink-3)',
    aspire_sync: '#1976d2',
    legacy_promote: '#795548',
    vendor_import: '#388e3c',
    aggregate_import: '#f57c00'
  }
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
      padding: '1px 6px', borderRadius: 3,
      background: 'var(--surface-2, #f6f1e3)',
      color: colors[source] || 'var(--ink-3)'
    }}>
      {labels[source] || source || 'unknown'}
    </span>
  )
}

const ASPIRE_BASE = 'https://cloud.youraspire.com/app/admin/estimating/catalog-items/material/details'

function AspireItemRow ({ item, onPromote, onFindMatch, promoting, matching }) {
  const cost = item.Cost ?? item.PurchaseUnitCost
  const isPromoting = promoting === item._id
  const isMatching = matching === item._id
  return (
    <div className="mat-row">
      <div className="mat-row-main">
        <div className="mat-row-title">
          <span className="mat-row-name">{item.ItemName}</span>
          {item.ItemCode && <span className="mat-row-meta mono" style={{ fontFamily: 'var(--f-mono)', fontSize: 11 }}>{item.ItemCode}</span>}
          {item.CatalogItemID && (
            <a
              href={`${ASPIRE_BASE}/${item.CatalogItemID}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ fontSize: 10, color: '#1976d2', textDecoration: 'none', fontWeight: 500 }}
              title="Open in Aspire"
            >
              Aspire ↗
            </a>
          )}
        </div>
        <div className="mat-row-sub">
          <span>{item.CategoryName || 'Uncategorized'}</span>
          <span className="dot-sep">·</span>
          <span>{item.UnitTypeName || 'Each'}</span>
          {cost != null && <><span className="dot-sep">·</span><span className="mono" style={{ fontFamily: 'var(--f-mono)' }}>{fmt$(cost)}</span></>}
          {item.ItemAlternateName && item.ItemAlternateName !== item.ItemName && (
            <><span className="dot-sep">·</span><span style={{ fontStyle: 'italic', color: 'var(--ink-4)' }}>{item.ItemAlternateName}</span></>
          )}
        </div>
      </div>
      <div className="mat-row-right" style={{ gap: 6 }}>
        {item.isPromoted ? (
          <span className="mat-pill mat-pill--ok"><span className="mat-pill-dot" />In catalog</span>
        ) : (
          <>
            <button
              className="hmi-btn hmi-btn-sm"
              style={{ fontSize: 11, fontWeight: 500 }}
              onClick={() => onFindMatch(item)}
              disabled={isMatching}
            >
              Find match
            </button>
            <button
              className="hmi-btn hmi-btn-primary hmi-btn-sm"
              disabled={isPromoting}
              onClick={() => onPromote(item._id)}
            >
              {isPromoting ? 'Promoting...' : 'Promote →'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

function MatchPanel ({ aspireItem, url, onLinked, onClose }) {
  const [q, setQ] = React.useState(aspireItem?.ItemName || '')
  const [results, setResults] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [linking, setLinking] = React.useState(null)

  React.useEffect(() => {
    if (!q || q.trim().length < 2) { setResults([]); return }
    setLoading(true)
    const timer = setTimeout(() => {
      axios.get(`${url}/api/bid-estimation/unified-catalog/items`, { params: { search: q, limit: 10 } })
        .then(res => { setResults(res.data.items || []); setLoading(false) })
        .catch(() => { setResults([]); setLoading(false) })
    }, 300)
    return () => clearTimeout(timer)
  }, [q, url])

  const handleLink = (catalogItem) => {
    setLinking(catalogItem._id)
    axios.post(`${url}/api/bid-estimation/unified-catalog/${catalogItem._id}/link-aspire`, { aspireItemId: aspireItem._id })
      .then(() => {
        toast.success(`Linked "${aspireItem.ItemName}" → "${catalogItem.canonicalName || catalogItem.description || '(item)'}"`)
        setLinking(null)
        onLinked()
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Link failed.')
        setLinking(null)
      })
  }

  return (
    <div style={{
      background: 'var(--surface-2, #f6f1e3)', border: '1px solid var(--line)',
      borderRadius: 8, padding: '12px 16px', marginBottom: 8
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>
          Find catalog match for: <span style={{ color: 'var(--accent)' }}>{aspireItem.ItemName}</span>
        </span>
        <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={onClose} style={{ fontSize: 11 }}>✕ Close</button>
      </div>
      <input
        type="text"
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Search Material Catalog..."
        autoFocus
        style={{
          width: '100%', height: 32, padding: '0 10px', borderRadius: 6,
          border: '1px solid var(--line-2)', fontSize: 13, marginBottom: 6,
          background: 'var(--bg-panel-alt)', outline: 'none'
        }}
      />
      {loading && <div style={{ padding: 8, textAlign: 'center' }}><CircularProgress size={18} /></div>}
      {!loading && results.length === 0 && q.length >= 2 && (
        <div style={{ padding: 8, fontSize: 12, color: 'var(--ink-3)' }}>No catalog items found. Use "Promote →" to create a new one.</div>
      )}
      {results.map(ci => (
        <div key={ci._id} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
          borderBottom: '1px solid var(--line)', fontSize: 13
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{ci.canonicalName || ci.description || '(unnamed)'}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              {cap(ci.category)} · {ci.unit}
              {ci.bestPrice > 0 && <> · {fmt$(ci.bestPrice)}</>}
              {ci.aspireItemIds?.length > 0 && <> · {ci.aspireItemIds.length} Aspire linked</>}
            </div>
          </div>
          <button
            className="hmi-btn hmi-btn-primary hmi-btn-sm"
            style={{ fontSize: 11 }}
            disabled={linking === ci._id}
            onClick={() => handleLink(ci)}
          >
            {linking === ci._id ? 'Linking...' : 'Link →'}
          </button>
        </div>
      ))}
    </div>
  )
}

const ASPIRE_DETAIL_BASE = 'https://cloud.youraspire.com/app/admin/estimating/catalog-items/material/details'
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

function InfoRow ({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 5 }}>
      <span style={{ fontSize: 11, color: 'var(--ink-3)', minWidth: 110, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 12.5, color: 'var(--ink)', fontWeight: 500, fontFamily: mono ? 'var(--f-mono)' : 'inherit' }}>{value || '—'}</span>
    </div>
  )
}

function SectionHead ({ title }) {
  return (
    <div style={{
      fontWeight: 600, fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase',
      color: 'var(--ink-3)', padding: '10px 0 5px', borderBottom: '1px solid var(--line)',
      marginBottom: 8, marginTop: 12
    }}>
      {title}
    </div>
  )
}

const FIELD_STYLE = { width: '100%', height: 30, padding: '0 8px', borderRadius: 6, border: '1px solid var(--line-2)', fontSize: 12, background: 'var(--bg-panel-alt)', outline: 'none' }
const CATEGORY_OPTIONS = ['irrigation', 'plant', 'aggregate', 'general', 'lighting', 'hardscape', 'herbicide', 'fertilizer']
const STATUS_OPTIONS = ['active', 'review', 'legacy', 'archived']

function DetailSidebar ({ item, url, onClose, onRefresh }) {
  const [detail, setDetail] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [imgError, setImgError] = React.useState({})
  const [editing, setEditing] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [unlinking, setUnlinking] = React.useState(null)
  const [form, setForm] = React.useState({})

  const reload = React.useCallback(() => {
    if (!item?._id) return
    setLoading(true)
    setImgError({})
    axios.get(`${url}/api/bid-estimation/unified-catalog/${item._id}/detail`)
      .then(res => { setDetail(res.data); setLoading(false) })
      .catch(() => { setDetail(null); setLoading(false) })
  }, [item?._id, url])

  React.useEffect(() => { reload() }, [reload])

  const d = detail || item
  const name = d?.canonicalName || d?.description || d?.key || '(unnamed)'
  const cov = ['covered', 'partial', 'missing'].includes(d?.coverageStatus) ? d.coverageStatus : 'missing'
  const aspireItems = detail?.aspireItemIds || []
  const vendorProducts = detail?.vendorCatalogProductIds || []
  const aggProfile = detail?.aggregateMaterialProfileId
  const aggGradation = detail?.aggregateGradationId

  const primaryImage = vendorProducts.reduce((found, vp) => {
    if (found) return found
    const primary = (vp.media || []).find(m => m.isPrimary && m.url)
    return primary || (vp.media || []).find(m => m.url)
  }, null)

  const startEdit = () => {
    setForm({
      canonicalName: d?.canonicalName || '',
      category: d?.category || 'general',
      subcategory: d?.subcategory || '',
      unit: d?.unit || 'Each',
      status: d?.status || 'active',
      aliases: (d?.aliases || []).join(', '),
      notes: d?.notes || '',
      wastePercent: d?.wastePercent ?? 0,
      manualOverride: d?.manualOverride ?? '',
      'attributes.diameter': d?.attributes?.diameter || '',
      'attributes.schedule': d?.attributes?.schedule || '',
      'attributes.sizeClass': d?.attributes?.sizeClass || '',
      'attributes.material': d?.attributes?.material || '',
      'attributes.color': d?.attributes?.color || '',
      'attributes.depth': d?.attributes?.depth || '',
      'attributes.manufacturer': d?.attributes?.manufacturer || '',
      'attributes.partNumber': d?.attributes?.partNumber || ''
    })
    setEditing(true)
  }

  const handleSave = () => {
    setSaving(true)
    const body = {
      canonicalName: form.canonicalName,
      category: form.category,
      subcategory: form.subcategory || null,
      unit: form.unit,
      status: form.status,
      aliases: form.aliases ? form.aliases.split(',').map(a => a.trim()).filter(Boolean) : [],
      notes: form.notes || null,
      wastePercent: Number(form.wastePercent) || 0,
      manualOverride: form.manualOverride !== '' ? Number(form.manualOverride) : null,
      attributes: {
        diameter: form['attributes.diameter'] || null,
        schedule: form['attributes.schedule'] || null,
        sizeClass: form['attributes.sizeClass'] || null,
        material: form['attributes.material'] || null,
        color: form['attributes.color'] || null,
        depth: form['attributes.depth'] || null,
        manufacturer: form['attributes.manufacturer'] || null,
        partNumber: form['attributes.partNumber'] || null
      }
    }
    axios.put(`${url}/api/bid-estimation/unified-catalog/${item._id}`, body)
      .then(() => {
        toast.success('Item updated.')
        setSaving(false)
        setEditing(false)
        reload()
        onRefresh()
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Save failed.')
        setSaving(false)
      })
  }

  const handleDelete = () => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return
    setDeleting(true)
    axios.delete(`${url}/api/bid-estimation/unified-catalog/${item._id}`)
      .then(() => {
        toast.success('Item deleted.')
        onRefresh()
        onClose()
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Delete failed.')
        setDeleting(false)
      })
  }

  const handleUnlinkAspire = (aspireItemId, aspireItemName) => {
    if (!window.confirm(`Remove Aspire link to "${aspireItemName}"?`)) return
    setUnlinking(aspireItemId)
    axios.post(`${url}/api/bid-estimation/unified-catalog/${item._id}/unlink-aspire`, { aspireItemId })
      .then(() => {
        toast.success('Aspire link removed.')
        setUnlinking(null)
        reload()
        onRefresh()
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Unlink failed.')
        setUnlinking(null)
      })
  }

  const handleUnlinkVendor = (vendorProductId, vendorProductName) => {
    if (!window.confirm(`Remove vendor product link to "${vendorProductName}"?`)) return
    setUnlinking(vendorProductId)
    axios.post(`${url}/api/bid-estimation/unified-catalog/${item._id}/unlink-vendor`, { vendorCatalogProductId: vendorProductId })
      .then(() => {
        toast.success('Vendor product link removed.')
        setUnlinking(null)
        reload()
        onRefresh()
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Unlink failed.')
        setUnlinking(null)
      })
  }

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '100vw',
      background: 'var(--bg-panel, #fff)', borderLeft: '1px solid var(--line)',
      boxShadow: '-4px 0 24px rgba(0,0,0,0.08)', zIndex: 1200,
      display: 'flex', flexDirection: 'column', overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px 12px', borderBottom: '1px solid var(--line)',
        display: 'flex', alignItems: 'flex-start', gap: 10
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', marginBottom: 4 }}>
            Material Detail
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.3, wordBreak: 'break-word' }}>
            {name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 3 }}>
            {cap(d?.category)} · {d?.unit}
            <span className={`mat-pill mat-pill--${cov === 'covered' ? 'ok' : cov === 'partial' ? 'warn' : 'risk'}`} style={{ marginLeft: 8 }}>
              <span className="mat-pill-dot" />{cap(cov)}
            </span>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none', border: 'none', fontSize: 18, cursor: 'pointer',
            color: 'var(--ink-3)', padding: '2px 6px', lineHeight: 1
          }}
        >✕</button>
      </div>

      {/* Action bar */}
      <div style={{ display: 'flex', gap: 6, padding: '8px 20px', borderBottom: '1px solid var(--line)' }}>
        {editing ? (
          <>
            <button className="hmi-btn hmi-btn-primary hmi-btn-sm" onClick={handleSave} disabled={saving} style={{ fontSize: 11 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={() => setEditing(false)} style={{ fontSize: 11 }}>Cancel</button>
          </>
        ) : (
          <>
            <button className="hmi-btn hmi-btn-sm" onClick={startEdit} style={{ fontSize: 11 }}>Edit</button>
            <div style={{ flex: 1 }} />
            <button
              className="hmi-btn hmi-btn-ghost hmi-btn-sm"
              onClick={handleDelete}
              disabled={deleting}
              style={{ fontSize: 11, color: 'var(--risk, #c62828)' }}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '12px 20px 24px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress size={24} /></div>
        ) : editing ? (
          <>
            <SectionHead title="Identity" />
            <EditField label="Canonical name" value={form.canonicalName} onChange={v => setField('canonicalName', v)} />
            <EditSelect label="Category" value={form.category} options={CATEGORY_OPTIONS} onChange={v => setField('category', v)} />
            <EditField label="Subcategory" value={form.subcategory} onChange={v => setField('subcategory', v)} />
            <EditField label="Unit" value={form.unit} onChange={v => setField('unit', v)} />
            <EditSelect label="Status" value={form.status} options={STATUS_OPTIONS} onChange={v => setField('status', v)} />
            <EditField label="Aliases" value={form.aliases} onChange={v => setField('aliases', v)} hint="Comma-separated" />
            <EditField label="Notes" value={form.notes} onChange={v => setField('notes', v)} />
            <EditField label="Waste %" value={form.wastePercent} onChange={v => setField('wastePercent', v)} type="number" />
            <SectionHead title="Pricing" />
            <EditField label="Manual override" value={form.manualOverride} onChange={v => setField('manualOverride', v)} type="number" hint="Leave empty for auto" />
            <SectionHead title="Attributes" />
            <EditField label="Diameter" value={form['attributes.diameter']} onChange={v => setField('attributes.diameter', v)} />
            <EditField label="Schedule" value={form['attributes.schedule']} onChange={v => setField('attributes.schedule', v)} />
            <EditField label="Size class" value={form['attributes.sizeClass']} onChange={v => setField('attributes.sizeClass', v)} />
            <EditField label="Material" value={form['attributes.material']} onChange={v => setField('attributes.material', v)} />
            <EditField label="Color" value={form['attributes.color']} onChange={v => setField('attributes.color', v)} />
            <EditField label="Depth" value={form['attributes.depth']} onChange={v => setField('attributes.depth', v)} />
            <EditField label="Manufacturer" value={form['attributes.manufacturer']} onChange={v => setField('attributes.manufacturer', v)} />
            <EditField label="Part number" value={form['attributes.partNumber']} onChange={v => setField('attributes.partNumber', v)} />
          </>
        ) : (
          <>
            {/* Image */}
            {primaryImage && !imgError[primaryImage.url] && (
              <div style={{ marginBottom: 12, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--line)', background: '#fafafa' }}>
                <img
                  src={primaryImage.url}
                  alt={name}
                  onError={() => setImgError(prev => ({ ...prev, [primaryImage.url]: true }))}
                  style={{ width: '100%', height: 'auto', maxHeight: 240, objectFit: 'contain', display: 'block' }}
                />
                {primaryImage.label && (
                  <div style={{ fontSize: 10, color: 'var(--ink-4)', padding: '4px 8px', textAlign: 'center' }}>{primaryImage.label}</div>
                )}
              </div>
            )}

            {/* Identity */}
            <SectionHead title="Identity" />
            <InfoRow label="Canonical name" value={d?.canonicalName} />
            {d?.subcategory && <InfoRow label="Subcategory" value={d.subcategory} />}
            <InfoRow label="Source" value={cap(d?.source?.replace(/_/g, ' '))} />
            <InfoRow label="Status" value={cap(d?.status)} />
            {d?.aliases?.length > 0 && <InfoRow label="Aliases" value={d.aliases.join(', ')} />}
            {d?.notes && <InfoRow label="Notes" value={d.notes} />}
            {d?.wastePercent > 0 && <InfoRow label="Waste %" value={`${d.wastePercent}%`} />}
            <InfoRow label="Created" value={fmtDate(d?.createdAt)} />
            <InfoRow label="Updated" value={fmtDate(d?.updatedAt)} />

            {/* Attributes */}
            {d?.attributes && Object.values(d.attributes).some(Boolean) && (
              <>
                <SectionHead title="Attributes" />
                {d.attributes.diameter && <InfoRow label="Diameter" value={d.attributes.diameter} />}
                {d.attributes.schedule && <InfoRow label="Schedule" value={d.attributes.schedule} />}
                {d.attributes.sizeClass && <InfoRow label="Size class" value={d.attributes.sizeClass} />}
                {d.attributes.material && <InfoRow label="Material" value={d.attributes.material} />}
                {d.attributes.color && <InfoRow label="Color" value={d.attributes.color} />}
                {d.attributes.depth && <InfoRow label="Depth" value={d.attributes.depth} />}
                {d.attributes.manufacturer && <InfoRow label="Manufacturer" value={d.attributes.manufacturer} />}
                {d.attributes.partNumber && <InfoRow label="Part number" value={d.attributes.partNumber} mono />}
              </>
            )}

            {/* Pricing */}
            <SectionHead title="Pricing" />
            <InfoRow label="Best price" value={d?.bestPrice > 0 ? `${fmt$(d.bestPrice)} / ${d.unit}` : '—'} mono />
            {d?.manualOverride != null && <InfoRow label="Manual override" value={fmt$(d.manualOverride)} mono />}
            {d?.vendorPrices?.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {d.vendorPrices.map((vp, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
                    borderBottom: i < d.vendorPrices.length - 1 ? '1px solid var(--line)' : 'none',
                    fontSize: 12
                  }}>
                    <span style={{ flex: 1, fontWeight: 500 }}>{vp.vendor}</span>
                    <span style={{ fontFamily: 'var(--f-mono)', fontWeight: 600 }}>{fmt$(vp.unitCost)}</span>
                    <span style={{ fontSize: 10, color: 'var(--ink-4)' }}>{fmtDate(vp.quoteDate)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Aspire Cross-References */}
            {aspireItems.length > 0 && (
              <>
                <SectionHead title={`Aspire Items (${aspireItems.length})`} />
                {aspireItems.map(ai => (
                  <div key={ai._id} style={{
                    padding: '6px 0', borderBottom: '1px solid var(--line)', fontSize: 12
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 600, flex: 1 }}>{ai.ItemName}</span>
                      {ai.CatalogItemID && (
                        <a
                          href={`${ASPIRE_DETAIL_BASE}/${ai.CatalogItemID}`}
                          target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 10, color: '#1976d2', textDecoration: 'none', fontWeight: 500 }}
                        >Aspire ↗</a>
                      )}
                      <button
                        className="hmi-btn hmi-btn-ghost hmi-btn-sm"
                        style={{ fontSize: 10, color: 'var(--risk, #c62828)', padding: '0 4px', height: 20 }}
                        disabled={unlinking === ai._id}
                        onClick={() => handleUnlinkAspire(ai._id, ai.ItemName)}
                      >
                        {unlinking === ai._id ? '...' : 'Unlink'}
                      </button>
                    </div>
                    <div style={{ color: 'var(--ink-3)', fontSize: 11, marginTop: 2 }}>
                      {ai.ItemCode && <span style={{ fontFamily: 'var(--f-mono)' }}>{ai.ItemCode}</span>}
                      {ai.CategoryName && <>{ai.ItemCode && ' · '}{ai.CategoryName}</>}
                      {ai.Cost != null && <> · {fmt$(ai.Cost)}</>}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Vendor Products */}
            {vendorProducts.length > 0 && (
              <>
                <SectionHead title={`Vendor Products (${vendorProducts.length})`} />
                {vendorProducts.map(vp => {
                  const vpImg = (vp.media || []).find(m => m.url)
                  return (
                    <div key={vp._id} style={{
                      padding: '8px 0', borderBottom: '1px solid var(--line)', fontSize: 12
                    }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {vpImg && !imgError[vpImg.url] && (
                          <img
                            src={vpImg.url}
                            alt={vp.productName}
                            onError={() => setImgError(prev => ({ ...prev, [vpImg.url]: true }))}
                            style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 4, border: '1px solid var(--line)', flexShrink: 0 }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 600, flex: 1 }}>{vp.productName}</span>
                            <button
                              className="hmi-btn hmi-btn-ghost hmi-btn-sm"
                              style={{ fontSize: 10, color: 'var(--risk, #c62828)', padding: '0 4px', height: 20 }}
                              disabled={unlinking === vp._id}
                              onClick={() => handleUnlinkVendor(vp._id, vp.productName)}
                            >
                              {unlinking === vp._id ? '...' : 'Unlink'}
                            </button>
                          </div>
                          <div style={{ color: 'var(--ink-3)', fontSize: 11 }}>
                            {vp.vendorName}
                            {vp.vendorItemNumber && <> · <span style={{ fontFamily: 'var(--f-mono)' }}>{vp.vendorItemNumber}</span></>}
                          </div>
                          {vp.shortDescription && (
                            <div style={{ color: 'var(--ink-4)', fontSize: 11, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {vp.shortDescription}
                            </div>
                          )}
                          {vp.pricing?.accountPrice != null && (
                            <div style={{ fontFamily: 'var(--f-mono)', fontWeight: 600, marginTop: 2 }}>
                              {fmt$(vp.pricing.accountPrice)}
                              {vp.pricing.regularPrice != null && vp.pricing.regularPrice !== vp.pricing.accountPrice && (
                                <span style={{ textDecoration: 'line-through', color: 'var(--ink-4)', fontWeight: 400, marginLeft: 6 }}>{fmt$(vp.pricing.regularPrice)}</span>
                              )}
                              {vp.pricing.unit && <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}> / {vp.pricing.unit}</span>}
                            </div>
                          )}
                          {vp.externalUrl && (
                            <a href={vp.externalUrl} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: 10, color: '#1976d2', textDecoration: 'none', marginTop: 3, display: 'inline-block' }}>
                              View product ↗
                            </a>
                          )}
                        </div>
                      </div>
                      {vp.specifications?.length > 0 && (
                        <div style={{ marginTop: 6, paddingLeft: vpImg ? 56 : 0 }}>
                          {vp.specifications.slice(0, 5).map((spec, si) => (
                            <div key={si} style={{ display: 'flex', gap: 6, fontSize: 10.5, color: 'var(--ink-3)' }}>
                              <span style={{ minWidth: 80, flexShrink: 0 }}>{spec.label || spec.code}</span>
                              <span style={{ color: 'var(--ink-2)' }}>{spec.value}</span>
                            </div>
                          ))}
                          {vp.specifications.length > 5 && (
                            <div style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 2 }}>+{vp.specifications.length - 5} more specs</div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </>
            )}

            {/* Aggregate */}
            {aggProfile && (
              <>
                <SectionHead title="Aggregate Source" />
                <InfoRow label="Profile" value={aggProfile.name} />
                {aggProfile.type && <InfoRow label="Type" value={cap(aggProfile.type)} />}
                {aggGradation && (
                  <>
                    <InfoRow label="Gradation" value={aggGradation.label} />
                    {aggGradation.sizeRange && <InfoRow label="Size range" value={aggGradation.sizeRange} />}
                  </>
                )}
              </>
            )}

            {/* Legacy */}
            {d?.legacyTakeoffId && (
              <>
                <SectionHead title="Legacy" />
                <InfoRow label="Takeoff ID" value={String(d.legacyTakeoffId)} mono />
                {d.key && <InfoRow label="Key" value={d.key} mono />}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function EditField ({ label, value, onChange, type, hint }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 3 }}>{label}</div>
      <input type={type || 'text'} value={value} onChange={e => onChange(e.target.value)} style={FIELD_STYLE} />
      {hint && <div style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 2 }}>{hint}</div>}
    </div>
  )
}

function EditSelect ({ label, value, options, onChange }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 3 }}>{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)} style={FIELD_STYLE}>
        {options.map(o => <option key={o} value={o}>{cap(o)}</option>)}
      </select>
    </div>
  )
}

function MaterialCatalogTab ({ url }) {
  const [view, setView] = React.useState('catalog')
  const [coverage, setCoverage] = React.useState(null)
  const [items, setItems] = React.useState([])
  const [total, setTotal] = React.useState(0)
  const [loading, setLoading] = React.useState(true)
  const [search, setSearch] = React.useState('')
  const [categoryFilter, setCategoryFilter] = React.useState('All')
  const [sourceFilter, setSourceFilter] = React.useState('')
  const [coverageFilter, setCoverageFilter] = React.useState('')
  const [aspireStatus, setAspireStatus] = React.useState('')
  const [page, setPage] = React.useState(0)
  const [promoting, setPromoting] = React.useState(null)
  const [bulkPromoting, setBulkPromoting] = React.useState(false)
  const [matchingItem, setMatchingItem] = React.useState(null)
  const [selectedItem, setSelectedItem] = React.useState(null)
  const [autoLinking, setAutoLinking] = React.useState(false)
  const pageSize = 50

  React.useEffect(() => {
    if (!selectedItem) return
    const handleKey = (e) => { if (e.key === 'Escape') setSelectedItem(null) }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [selectedItem])

  const fetchCoverage = React.useCallback(() => {
    axios.get(`${url}/api/bid-estimation/unified-catalog/coverage`)
      .then(res => setCoverage(res.data))
      .catch(() => {})
  }, [url])

  const fetchItems = React.useCallback(() => {
    setLoading(true)
    const cat = categoryFilter !== 'All' ? categoryFilter.toLowerCase() : ''

    if (view === 'catalog') {
      const params = { limit: pageSize, skip: page * pageSize }
      if (search) params.search = search
      if (cat) params.category = cat
      if (sourceFilter) params.source = sourceFilter
      if (coverageFilter) params.coverage = coverageFilter
      axios.get(`${url}/api/bid-estimation/unified-catalog/items`, { params })
        .then(res => { setItems(res.data.items || []); setTotal(res.data.total || 0); setLoading(false) })
        .catch(() => { setItems([]); setTotal(0); setLoading(false) })
    } else {
      const params = { limit: pageSize, skip: page * pageSize }
      if (search) params.search = search
      if (cat) params.category = cat
      if (aspireStatus) params.status = aspireStatus
      axios.get(`${url}/api/bid-estimation/unified-catalog/aspire-items`, { params })
        .then(res => { setItems(res.data.items || []); setTotal(res.data.total || 0); setLoading(false) })
        .catch(() => { setItems([]); setTotal(0); setLoading(false) })
    }
  }, [url, view, search, categoryFilter, sourceFilter, coverageFilter, aspireStatus, page])

  React.useEffect(() => { fetchCoverage() }, [fetchCoverage])

  React.useEffect(() => {
    const timer = setTimeout(() => { setPage(0); fetchItems() }, 300)
    return () => clearTimeout(timer)
  }, [search, categoryFilter, sourceFilter, coverageFilter, aspireStatus, view])

  React.useEffect(() => {
    fetchItems()
  }, [page])

  const handlePromote = (aspireItemId) => {
    setPromoting(aspireItemId)
    axios.post(`${url}/api/bid-estimation/unified-catalog/promote`, { aspireItemId })
      .then(() => {
        setPromoting(null)
        toast.success('Promoted to Material Catalog.')
        fetchItems()
        fetchCoverage()
      })
      .catch(err => {
        setPromoting(null)
        toast.error(err.response?.data?.error || 'Promote failed.')
      })
  }

  const handleBulkPromote = () => {
    const count = items.filter(i => !i.isPromoted).length
    if (!count) return
    if (!window.confirm(`Promote ${count} visible unpromoted Aspire items to Material Catalog?`)) return
    setBulkPromoting(true)
    const unpromoted = items.filter(i => !i.isPromoted)
    let done = 0
    let errors = 0
    const process = async () => {
      for (const item of unpromoted) {
        try {
          await axios.post(`${url}/api/bid-estimation/unified-catalog/promote`, { aspireItemId: item._id })
          done++
        } catch (_) { errors++ }
      }
      setBulkPromoting(false)
      toast.success(`Promoted ${done} items${errors ? `, ${errors} failed` : ''}.`)
      fetchItems()
      fetchCoverage()
    }
    process()
  }

  const handleAutoLinkSku = () => {
    setAutoLinking(true)
    axios.post(`${url}/api/bid-estimation/unified-catalog/auto-link-sku`)
      .then(res => {
        setAutoLinking(false)
        toast.success(`Scanned ${res.data.scanned} items, auto-linked ${res.data.linked} by SKU.`)
        fetchItems()
        fetchCoverage()
      })
      .catch(err => {
        setAutoLinking(false)
        toast.error(err.response?.data?.error || 'Auto-link failed.')
      })
  }

  const totalPages = Math.ceil(total / pageSize)

  return (
    <div className="hmi-page" style={{ position: 'relative', minHeight: '100vh' }}>
      <div className="hmi-body">
        {/* Stats Strip */}
        {coverage && (
          <div className="mat-stats">
            <Stat label="Material Catalog" value={String(coverage.catalog.total)} caption={`${coverage.catalog.withPricing} with pricing`} tone="accent" />
            <Stat label="Aspire Promoted" value={String(coverage.aspire.promoted)} caption={`of ${coverage.aspire.total.toLocaleString()} items`} tone="ok" />
            <Stat label="Aspire Unpromoted" value={coverage.aspire.unpromoted.toLocaleString()} caption="not in our catalog yet" tone={coverage.aspire.unpromoted > 0 ? 'warn' : null} />
            <Stat label="Coverage" value={`${coverage.aspire.coveragePercent}%`} caption="Aspire → Catalog" />
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', paddingRight: 8 }}>
              <button className="hmi-btn hmi-btn-sm" onClick={handleAutoLinkSku} disabled={autoLinking} style={{ fontSize: 11 }}>
                {autoLinking ? 'Linking...' : 'Auto-link by SKU'}
              </button>
            </div>
          </div>
        )}

        {/* View Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 2 }}>
            <button
              className={`hmi-btn hmi-btn-sm ${view === 'catalog' ? '' : 'hmi-btn-ghost'}`}
              style={view === 'catalog' ? { background: 'var(--brand, var(--primary))', color: '#fff', fontWeight: 600, borderColor: 'transparent' } : { fontWeight: 500 }}
              onClick={() => { setView('catalog'); setPage(0) }}
            >
              Our Catalog
            </button>
            <button
              className={`hmi-btn hmi-btn-sm ${view === 'aspire' ? '' : 'hmi-btn-ghost'}`}
              style={view === 'aspire' ? { background: 'var(--brand, var(--primary))', color: '#fff', fontWeight: 600, borderColor: 'transparent' } : { fontWeight: 500 }}
              onClick={() => { setView('aspire'); setPage(0) }}
            >
              Aspire Items
            </button>
          </div>

          <div style={{ width: 1, height: 20, background: 'var(--line)' }} />

          {/* Category chips */}
          <div style={{ display: 'flex', gap: 2 }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`hmi-btn hmi-btn-sm ${categoryFilter === cat ? '' : 'hmi-btn-ghost'}`}
                style={categoryFilter === cat ? { background: 'var(--brand, var(--primary))', color: '#fff', fontWeight: 600, borderColor: 'transparent' } : { fontWeight: 500 }}
                onClick={() => setCategoryFilter(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Search */}
          <div style={{ marginLeft: 'auto', position: 'relative', width: 260 }}>
            <input
              type="text"
              placeholder="Search materials, aliases..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', height: 34, padding: '0 12px', borderRadius: 8,
                border: '1px solid var(--line-2)', background: 'var(--bg-panel-alt)',
                fontSize: 13, outline: 'none', fontFamily: 'var(--f-sans, var(--font-ui))'
              }}
            />
          </div>
        </div>

        {/* Extra filters */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {view === 'catalog' && (
            <>
              <select
                value={sourceFilter}
                onChange={e => setSourceFilter(e.target.value)}
                style={{ height: 30, borderRadius: 6, border: '1px solid var(--line-2)', fontSize: 12, padding: '0 8px', background: 'var(--bg-panel-alt)' }}
              >
                {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select
                value={coverageFilter}
                onChange={e => setCoverageFilter(e.target.value)}
                style={{ height: 30, borderRadius: 6, border: '1px solid var(--line-2)', fontSize: 12, padding: '0 8px', background: 'var(--bg-panel-alt)' }}
              >
                <option value="">All pricing</option>
                <option value="covered">Has pricing</option>
                <option value="missing">No pricing</option>
              </select>
            </>
          )}
          {view === 'aspire' && (
            <>
              <select
                value={aspireStatus}
                onChange={e => setAspireStatus(e.target.value)}
                style={{ height: 30, borderRadius: 6, border: '1px solid var(--line-2)', fontSize: 12, padding: '0 8px', background: 'var(--bg-panel-alt)' }}
              >
                <option value="">All items</option>
                <option value="promoted">Promoted (in catalog)</option>
                <option value="unpromoted">Not promoted</option>
              </select>
              {aspireStatus === 'unpromoted' && items.some(i => !i.isPromoted) && (
                <button className="hmi-btn hmi-btn-primary hmi-btn-sm" onClick={handleBulkPromote} disabled={bulkPromoting}>
                  {bulkPromoting ? 'Promoting...' : `Promote visible (${items.filter(i => !i.isPromoted).length})`}
                </button>
              )}
            </>
          )}

          {/* Source breakdown chips */}
          {view === 'catalog' && coverage && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
              {Object.entries(coverage.catalog.bySource).map(([src, count]) => (
                <button
                  key={src}
                  className={`hmi-btn hmi-btn-sm hmi-btn-ghost`}
                  style={{ fontSize: 11, padding: '0 6px', height: 22, opacity: sourceFilter === src ? 1 : 0.6 }}
                  onClick={() => setSourceFilter(sourceFilter === src ? '' : src)}
                >
                  <SourceBadge source={src} /> {count}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results */}
        {loading && items.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress /></div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)' }}>
            {search ? `No results for "${search}"` : 'No items found with these filters.'}
          </div>
        ) : (
          <>
            <Section tone={view === 'catalog' ? 'accent' : 'ok'}
              icon={view === 'catalog' ? '◈' : '▣'}
              title={view === 'catalog' ? 'Material Catalog Items' : 'Aspire Catalog Items'}
              count={total}
              note={view === 'catalog'
                ? `Showing ${items.length} of ${total.toLocaleString()}`
                : `${items.filter(i => i.isPromoted).length} promoted on this page`
              }
            >
              {view === 'catalog'
                ? items.map(item => <CatalogItemRow key={item._id} item={item} onEdit={setSelectedItem} />)
                : items.map(item => (
                  <React.Fragment key={item._id}>
                    <AspireItemRow
                      item={item}
                      onPromote={handlePromote}
                      promoting={promoting}
                      onFindMatch={(it) => setMatchingItem(matchingItem?._id === it._id ? null : it)}
                      matching={matchingItem?._id}
                    />
                    {matchingItem?._id === item._id && (
                      <MatchPanel
                        aspireItem={item}
                        url={url}
                        onLinked={() => { setMatchingItem(null); fetchItems(); fetchCoverage() }}
                        onClose={() => setMatchingItem(null)}
                      />
                    )}
                  </React.Fragment>
                ))
              }
            </Section>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={() => setPage(p => p - 1)} disabled={page === 0}>← Prev</button>
                <span style={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--ink-2)' }}>
                  {page + 1} of {totalPages}
                </span>
                <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Sidebar */}
      {selectedItem && (
        <>
          <div
            onClick={() => setSelectedItem(null)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.25)', zIndex: 1199
            }}
          />
          <DetailSidebar
            item={selectedItem}
            url={url}
            onClose={() => setSelectedItem(null)}
            onRefresh={() => { fetchItems(); fetchCoverage() }}
          />
        </>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(MaterialCatalogTab)
